import { DollarSign } from "lucide-react";
import { useAgent } from "@/client/hooks/use-agent";
import { cn } from "@/client/lib/utils";

function formatUsd(value: number): string {
  if (value < 0.01) return "$0.00";
  return `$${value.toFixed(2)}`;
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

export function CostChip(): React.ReactElement {
  const { dailyCostUsd, dailyCapUsd, lastTokensIn, lastTokensOut, lastCostUsd } = useAgent();

  const pct = dailyCapUsd > 0 ? dailyCostUsd / dailyCapUsd : 0;
  const isWarn = pct >= 0.8 && pct < 1;
  const isCapped = dailyCapUsd === 0 || pct >= 1;

  const chipLabel = dailyCapUsd === 0 ? "Quiet mode" : `${formatUsd(dailyCostUsd)} / ${formatUsd(dailyCapUsd)}`;

  const tooltipLines = [
    `Daily: ${formatUsd(dailyCostUsd)} of ${formatUsd(dailyCapUsd)} cap`,
    lastCostUsd > 0
      ? `Last turn: ${formatTokens(lastTokensIn)} in · ${formatTokens(lastTokensOut)} out · ${formatUsd(lastCostUsd)}`
      : null,
  ].filter(Boolean);

  return (
    <div className="relative group" data-testid="agent-cost-chip">
      <span
        role="status"
        className={cn(
          "flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium border transition-colors cursor-default select-none",
          isCapped && dailyCapUsd > 0
            ? "bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-400"
            : isWarn
              ? "bg-amber-100 border-amber-300 text-amber-700 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-400"
              : "bg-muted border-border text-muted-foreground",
        )}
        aria-label={`AI cost: ${chipLabel}`}
      >
        <DollarSign className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
        <span aria-hidden="true">{chipLabel}</span>
      </span>

      {/* Tooltip */}
      <div
        className="absolute right-0 top-full mt-1 z-50 hidden group-hover:block w-max max-w-[220px] rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
        role="tooltip"
        data-testid="agent-cost-tooltip"
      >
        {tooltipLines.map((line) => (
          <div key={line}>{line}</div>
        ))}
        {isCapped && dailyCapUsd > 0 && (
          <div className="mt-1 font-medium text-red-600 dark:text-red-400">Daily cap reached — chat disabled</div>
        )}
      </div>
    </div>
  );
}
